/* import __COLOCATED_TEMPLATE__ from './seller-profile-header.hbs'; */
import Component from '@glimmer/component';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import clsx from 'clsx';
import { htmlSafe } from '@ember/template';

/**
 * Different counts for the truncation of the description string on different browser sizes.  This is to get
 * multi-line support then adding ellipses rather than just having browser do this on a single line.
 *
 * @type {number}
 */
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE = 50;
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN = 200;
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP = 250;

const CLASS_NAME = 'SH-c-seller-profile-freelancer-profile__header';
const NETWORK_STATUS_CLASS_NAME = `${CLASS_NAME}__network-status`;
const IN_NETWORK_STATUS_CLASS_NAME = `${NETWORK_STATUS_CLASS_NAME}--in`;

export default class SellerProfileHeader extends Component {
  @service session;
  @service media;

  @tracked isDescriptionFullyExpanded = false;
  @tracked isDescriptionTruncated = true;

  #extendedNetwork = createCache(() => {
    return this.args.user.companySellerConnections.reduce((extendedNetwork, connection) => {
      if (connection.invited) {
        extendedNetwork.push(connection);
      }
      return extendedNetwork;
    }, []);
  });

  get className() {
    return CLASS_NAME;
  }

  get networkStatusClassNames() {
    return clsx(
      NETWORK_STATUS_CLASS_NAME,
      this.isInNetwork && IN_NETWORK_STATUS_CLASS_NAME
    );
  }

  get isInNetwork() {
    return this.args.currentCompanyConnection?.invited ?? false;
  }

  get extendedNetwork() {
    return getValue(this.#extendedNetwork);
  }

  get extendedNetworkCompanyName() {
    if (this.extendedNetwork.length > 1) {
      return 'Multiple companies'
    }

    return this.extendedNetwork?.[0].company.name ?? null;
  }

  get rating() {
    return this.isInNetwork
      ? (this.args.currentCompanyConnection?.rating ?? 0)
      : this.args.user.mrating;
  }

  get reviewRatingFloat() {
    return this.rating > 0 ? this.rating.toFixed(1) : 0;
  }

  get projectCount() {
    if (this.args.activeProjectsCount == null || this.args.completedProjectsCount == null) return -1;

    return this.args.activeProjectsCount + this.args.completedProjectsCount;
  }

  // The description text will be truncated or not based on isDescriptionFullyExpanded.
  get description() {
    let descriptionText = this.args.user.description ?? '';

    if (this.isDescriptionFullyExpanded) {
      return htmlSafe(descriptionText);
    }

    if (this.media.isMobile) {
      if (descriptionText.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE) {
        this.isDescriptionTruncated = false;
      } else {
        this.isDescriptionTruncated = true;
        descriptionText = descriptionText.slice(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE) + '...';
      }
    }

    else if (this.media.isMediumAndDown) {
      if (descriptionText.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN) {
        this.isDescriptionTruncated = false;
      } else {
        this.isDescriptionTruncated = true;
        descriptionText = descriptionText.slice(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN) + '...';
      }
    }

    else if (descriptionText.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP) {
      this.isDescriptionTruncated = false;
    }

    else {
      this.isDescriptionTruncated = true;
      descriptionText = descriptionText.slice(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP) + '...';
    }

    return htmlSafe(descriptionText);
  }

  get backgroundButtonText() {
    return this.isDescriptionFullyExpanded ? 'Show less' : 'Read more';
  }

  @action
  onShowMoreOrLessClicked(e) {
    e.stopPropagation();
    e.preventDefault();
    this.isDescriptionFullyExpanded = !this.isDescriptionFullyExpanded;
  }
}
